/**
 * Client-ready share guard for Studio preview/share CTAs.
 *
 * Separate from preview-open-guard: agency self-review (open preview) may remain
 * allowed on QA_PASSED_WITH_WARNINGS, while client-ready share must be discouraged
 * or acknowledgement-gated. Does not change quality gate status math.
 */

export const CLIENT_SHARE_QA_FAILED_MESSAGE =
  "Generation did not pass quality gate. Resolve quality issues before sharing with your client.";

export const CLIENT_SHARE_QA_WARN_MESSAGE =
  "Preview generated with warnings. Not client-ready until quality issues are resolved.";

export const CLIENT_SHARE_QA_WARN_ACK_LABEL =
  "I understand this preview has unresolved quality warnings and is not client-ready.";

export const CLIENT_SHARE_QA_WARN_PRIMARY_CTA = "Resolve quality issues";

export interface ClientShareGuardInput {
  readonly latestRunStatus: string | null | undefined;
  readonly qaFailure: boolean;
  /** When true, caller has collected explicit acknowledgement for warn-pass share. */
  readonly warnShareAcknowledged?: boolean;
}

export function isQaWarnRunStatus(status: string | null | undefined): boolean {
  return status === "QA_PASSED_WITH_WARNINGS";
}

export function requiresWarnShareAcknowledgement(input: ClientShareGuardInput): boolean {
  if (input.qaFailure) return false;
  return isQaWarnRunStatus(input.latestRunStatus);
}

/**
 * Hard block for client-ready share (copy preview link / share client link in preview context).
 * Returns null when share is allowed (including warn-pass after acknowledgement).
 */
export function getClientShareBlockReason(input: ClientShareGuardInput): string | null {
  if (input.qaFailure || input.latestRunStatus === "QA_FAILED" || input.latestRunStatus === "FAILED") {
    return CLIENT_SHARE_QA_FAILED_MESSAGE;
  }
  if (isQaWarnRunStatus(input.latestRunStatus) && !input.warnShareAcknowledged) {
    return CLIENT_SHARE_QA_WARN_MESSAGE;
  }
  return null;
}

export function isClientReadyShareAllowed(input: ClientShareGuardInput): boolean {
  return getClientShareBlockReason(input) === null;
}

/** Hero primary should not imply client-ready share when warnings remain. */
export function shouldOverrideHeroForQaWarn(input: ClientShareGuardInput): boolean {
  return requiresWarnShareAcknowledgement(input);
}
